import { z } from 'zod';

/**
 * Karta pracownika: dane kadrowe potrzebne w procesach (adres, forma współpracy, kontakt awaryjny,
 * uprawnienia zawodowe z terminami). Osobno od konta logowania; widzi i zmienia je tylko zarządzający pracownikami.
 */

export const EMPLOYMENT_TYPES = {
  umowa_o_prace: 'Umowa o pracę',
  zlecenie: 'Umowa zlecenie',
  dzielo: 'Umowa o dzieło',
  b2b: 'Współpraca B2B',
  podwykonawca: 'Podwykonawca (firma)',
} as const;
export type EmploymentType = keyof typeof EMPLOYMENT_TYPES;

/** Uprawnienia i badania, od których zależy praca w branżach (F-gaz — klimatyzacja i pompy ciepła, SEP — elektryka, PV). */
export const QUALIFICATION_KINDS = {
  sep_e: 'SEP E (eksploatacja)',
  sep_d: 'SEP D (dozór)',
  fgaz: 'Certyfikat F-gazowy',
  udt: 'UDT (podesty, wózki)',
  wysokosc: 'Praca na wysokości',
  badania: 'Badania lekarskie',
  bhp: 'Szkolenie BHP',
  inne: 'Inne',
} as const;
export type QualificationKind = keyof typeof QUALIFICATION_KINDS;

/** Ostrzeżenie o kończącym się terminie — tyle dni przed datą. */
export const QUALIFICATION_WARN_DAYS = 30;

export interface QualificationDto {
  kind: QualificationKind;
  /** nazwa przy „Inne”, opcjonalny opis przy pozostałych */
  name: string;
  number: string;
  /** RRRR-MM-DD albo null (bezterminowe) */
  validUntil: string | null;
}

export interface EmployeeProfileDto {
  userId: string;
  position: string;
  employmentType: EmploymentType | null;
  hiredAt: string | null;
  street: string;
  postalCode: string;
  city: string;
  privatePhone: string;
  emergencyName: string;
  emergencyPhone: string;
  drivingLicence: string;
  vehiclePlate: string;
  qualifications: QualificationDto[];
  notes: string;
  updatedAt: string | null;
}

const text = (max: number) => z.string().trim().max(max).default('');
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Niepoprawna data');

export const employeeProfileSchema = z.object({
  position: text(80),
  employmentType: z.enum(Object.keys(EMPLOYMENT_TYPES) as [EmploymentType, ...EmploymentType[]]).nullable().default(null),
  hiredAt: day.nullable().default(null),
  street: text(120),
  postalCode: z.string().trim().max(10).regex(/^(\d{2}-\d{3})?$/, 'Kod pocztowy w formacie 00-000').default(''),
  city: text(80),
  privatePhone: text(30),
  emergencyName: text(120),
  emergencyPhone: text(30),
  drivingLicence: text(40),
  vehiclePlate: text(15),
  qualifications: z
    .array(
      z.object({
        kind: z.enum(Object.keys(QUALIFICATION_KINDS) as [QualificationKind, ...QualificationKind[]]),
        name: text(80),
        number: text(60),
        validUntil: day.nullable().default(null),
      }),
    )
    .max(30)
    .default([]),
  notes: text(2000),
});
export type EmployeeProfileInput = z.infer<typeof employeeProfileSchema>;

/** Stan terminu uprawnienia względem dnia (RRRR-MM-DD). */
export function qualificationState(validUntil: string | null, today: string): 'ok' | 'wygasa' | 'niewazne' {
  if (!validUntil) return 'ok';
  if (validUntil < today) return 'niewazne';
  const warn = new Date(`${today}T00:00:00Z`);
  warn.setUTCDate(warn.getUTCDate() + QUALIFICATION_WARN_DAYS);
  return validUntil <= warn.toISOString().slice(0, 10) ? 'wygasa' : 'ok';
}

// ── konto (login) ───────────────────────────────────────────────────────────

/** Login: małe litery, cyfry, kropka, podkreślenie, myślnik (3–40 znaków) — nie musi być e-mailem. */
export const LOGIN_PATTERN = /^[a-z0-9][a-z0-9._-]{2,39}$/;

const PL: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };
const plain = (s: string) =>
  s
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (c) => PL[c] ?? c)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');

/** Podpowiedź loginu: pierwsza litera imienia + nazwisko (Łukasz Bek → lbek). */
export function suggestLogin(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const first = plain(parts[0]!).slice(0, 1);
  const last = parts.length > 1 ? plain(parts[parts.length - 1]!) : plain(parts[0]!).slice(1);
  return (first + last).slice(0, 36);
}
